/**
 * Deferred video / iframe loading for homepage media sections.
 * - Unloads sources on non-matching viewport wrappers (desktop vs mobile)
 * - Plays only active / intersecting videos
 * - Activates YouTube/Vimeo iframes via data-src when needed
 */
(function () {
  var MOBILE_MQ = '(max-width: 749px)';

  function isMobileViewport() {
    return window.matchMedia(MOBILE_MQ).matches;
  }

  function storeAndUnloadVideo(video) {
    if (!(video instanceof HTMLVideoElement) || video.dataset.mediaLazyUnloaded === 'true') return;

    var sources = Array.prototype.slice.call(video.querySelectorAll('source')).map(function (source) {
      return { src: source.getAttribute('src') || '', type: source.getAttribute('type') || '' };
    });

    video.dataset.mediaLazySrc = video.getAttribute('src') || '';
    video.dataset.mediaLazySources = JSON.stringify(sources);
    video.removeAttribute('src');
    video.querySelectorAll('source').forEach(function (source) {
      source.removeAttribute('src');
    });
    video.load();
    video.dataset.mediaLazyUnloaded = 'true';
  }

  function restoreVideo(video) {
    if (!(video instanceof HTMLVideoElement) || video.dataset.mediaLazyUnloaded !== 'true') return;

    var sources = [];
    try {
      sources = JSON.parse(video.dataset.mediaLazySources || '[]');
    } catch (e) {
      sources = [];
    }

    if (sources.length) {
      var existing = video.querySelectorAll('source');
      if (existing.length === sources.length) {
        existing.forEach(function (source, index) {
          if (sources[index].src) source.setAttribute('src', sources[index].src);
          if (sources[index].type) source.setAttribute('type', sources[index].type);
        });
      } else {
        video.innerHTML = '';
        sources.forEach(function (item) {
          if (!item.src) return;
          var source = document.createElement('source');
          source.src = item.src;
          if (item.type) source.type = item.type;
          video.appendChild(source);
        });
      }
    } else if (video.dataset.mediaLazySrc) {
      video.setAttribute('src', video.dataset.mediaLazySrc);
    }

    video.load();
    delete video.dataset.mediaLazyUnloaded;
  }

  function activateIframe(iframe) {
    if (!(iframe instanceof HTMLIFrameElement)) return;
    var dataSrc = iframe.getAttribute('data-src');
    if (dataSrc && iframe.getAttribute('src') !== dataSrc) {
      iframe.setAttribute('src', dataSrc);
    }
  }

  function deactivateIframe(iframe) {
    if (!(iframe instanceof HTMLIFrameElement)) return;
    if (iframe.hasAttribute('src')) {
      iframe.removeAttribute('src');
    }
  }

  function syncViewportMedia(root) {
    var mobile = isMobileViewport();
    var scopes = root
      ? [root]
      : Array.prototype.slice.call(document.querySelectorAll('[data-lazy-media]'));

    scopes.forEach(function (mediaRoot) {
      var slide = mediaRoot.closest('.splide__slide');
      var slider = mediaRoot.closest('[data-customdown-slider]');
      var isInactiveSliderSlide = false;

      if (slide && slider) {
        var slides = slider.querySelectorAll('.splide__slide');
        var activeSlide =
          slider.querySelector('.splide__slide.is-active') ||
          slider.querySelector('.splide__slide.is-visible') ||
          slides[0];
        if (activeSlide && slide !== activeSlide) {
          isInactiveSliderSlide = true;
        }
      }

      mediaRoot.querySelectorAll('[data-media-viewport]').forEach(function (wrap) {
        var isMobileWrap = wrap.getAttribute('data-media-viewport') === 'mobile';
        var matches = mobile === isMobileWrap;

        wrap.querySelectorAll('video.media-lazy__video, video').forEach(function (video) {
          if (isInactiveSliderSlide || !matches) {
            video.pause();
            storeAndUnloadVideo(video);
          } else {
            restoreVideo(video);
          }
        });

        wrap.querySelectorAll('iframe.media-lazy__iframe').forEach(function (iframe) {
          if (isInactiveSliderSlide || !matches) deactivateIframe(iframe);
        });
      });
    });
  }

  function pauseAllIn(root) {
    if (!root) return;
    root.querySelectorAll('video').forEach(function (video) {
      video.pause();
    });
    root.querySelectorAll('iframe.media-lazy__iframe').forEach(deactivateIframe);
  }

  function playActiveIn(root) {
    if (!root) return;
    syncViewportMedia(root);

    var mobile = isMobileViewport();
    var selector = mobile ? '[data-media-viewport="mobile"]' : '[data-media-viewport="desktop"]';
    var wrap = root.querySelector(selector) || root;

    wrap.querySelectorAll('video.media-lazy__video, video').forEach(function (video) {
      restoreVideo(video);
      video.muted = true;
      var playPromise = video.play();
      if (playPromise && typeof playPromise.catch === 'function') {
        playPromise.catch(function () {});
      }
    });

    wrap.querySelectorAll('iframe.media-lazy__iframe').forEach(activateIframe);
  }

  function setActiveSlide(sliderEl, activeIndex) {
    if (!sliderEl) return;
    var slides = sliderEl.querySelectorAll('.splide__slide');
    slides.forEach(function (slide, index) {
      var mediaRoot = slide.querySelector('[data-lazy-media]') || slide;
      if (index === activeIndex) {
        playActiveIn(mediaRoot);
      } else {
        pauseAllIn(mediaRoot);
        mediaRoot.querySelectorAll('video').forEach(storeAndUnloadVideo);
      }
    });
  }

  function observeSection(sectionEl) {
    if (!sectionEl || sectionEl.dataset.mediaLazyObserved === 'true') return;
    sectionEl.dataset.mediaLazyObserved = 'true';

    var mediaRoot = sectionEl.querySelector('[data-lazy-media]') || sectionEl;
    syncViewportMedia(mediaRoot);

    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting && entry.intersectionRatio >= 0.4) {
            playActiveIn(mediaRoot);
          } else {
            pauseAllIn(mediaRoot);
            mediaRoot.querySelectorAll('video').forEach(storeAndUnloadVideo);
          }
        });
      },
      { threshold: [0, 0.4, 0.55, 1] }
    );

    observer.observe(sectionEl);
  }

  function initStandaloneSections() {
    document.querySelectorAll('[data-lazy-media]').forEach(function (mediaRoot) {
      if (mediaRoot.closest('[data-customdown-slider]')) return;
      var section = mediaRoot.closest('section') || mediaRoot.closest('.video-section') || mediaRoot.parentElement;
      if (section) observeSection(section);
    });
  }

  function init() {
    syncViewportMedia();
    initStandaloneSections();
  }

  window.MediaLazy = {
    syncViewportMedia: syncViewportMedia,
    playActiveIn: playActiveIn,
    pauseAllIn: pauseAllIn,
    setActiveSlide: setActiveSlide,
    isMobileViewport: isMobileViewport,
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  var resizeTimer;
  window.addEventListener(
    'resize',
    function () {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(function () {
        syncViewportMedia();
      }, 150);
    },
    { passive: true }
  );
})();
